import React, { useState, useEffect, useRef } from "react";
import InventoryPage from "./pages/InventoryPage";
import CartPage from "./pages/CartPage";
import HistoryPage from "./pages/HistoryPage";
import MealPlanPage from "./pages/MealPlanPage";
import MealsPage from "./pages/MealsPage";
import SharedPage from "./pages/SharedPage";
import HouseholdSetup from "./pages/HouseholdSetup";
import HouseholdSettings from "./components/HouseholdSettings";
import { sharingApi } from "./services/api";
import "./App.css";

const NAV_ITEMS = [
  { key: "mealplan", label: "Meal Plan", icon: "📅" },
  { key: "meals", label: "Meals", icon: "🍽" },
  { key: "inventory", label: "Inventory", icon: "📦" },
  { key: "cart", label: "Cart", icon: "🛒" },
  { key: "shared", label: "Shared", icon: "📬" },
  { key: "history", label: "History", icon: "📋" },
];

export default function App() {
  const [page, setPage] = useState("mealplan");
  const [household, setHousehold] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [newSharedCount, setNewSharedCount] = useState(0);
  const [installPrompt, setInstallPrompt] = useState(null);
  const [showInstall, setShowInstall] = useState(false);
  const drawerRef = useRef(null);

  useEffect(() => {
    const id = localStorage.getItem("household_id");
    const name = localStorage.getItem("household_name");
    const code = localStorage.getItem("household_code");
    const username = localStorage.getItem("household_username");
    if (id && name && code) setHousehold({ id, name, invite_code: code, username });
    setLoading(false);

    window.addEventListener("beforeinstallprompt", (e) => {
      e.preventDefault();
      setInstallPrompt(e);
      setShowInstall(true);
    });
  }, []);

  // Close drawer on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) {
        setDrawerOpen(false);
      }
    };
    if (drawerOpen) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [drawerOpen]);

  useEffect(() => {
    if (!household) return;
    const checkShared = async () => {
      try {
        const received = await sharingApi.getReceived();
        setNewSharedCount(received.filter((r) => !r.is_imported).length);
      } catch {}
    };
    checkShared();
    const interval = setInterval(checkShared, 300000);
    return () => clearInterval(interval);
  }, [household]);

  const handleInstall = async () => {
    if (!installPrompt) return;
    installPrompt.prompt();
    const { outcome } = await installPrompt.userChoice;
    if (outcome === "accepted") setShowInstall(false);
  };

  const handleNavClick = (key) => {
    setPage(key);
    setDrawerOpen(false);
    if (key === "shared") setNewSharedCount(0);
  };

  const handleJoined = (h) => setHousehold(h);
  const handleLeave = () => setHousehold(null);

  if (loading) return null;
  if (!household) return <HouseholdSetup onJoined={handleJoined} />;

  const currentNav = NAV_ITEMS.find(n => n.key === page);

  return (
    <div className="app">
      {showInstall && (
        <div className="install-banner">
          <span>📱 Install HomeStock on your device</span>
          <div className="install-actions">
            <button className="install-btn" onClick={handleInstall}>Install</button>
            <button className="install-dismiss" onClick={() => setShowInstall(false)}>✕</button>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="header">
        <div className="header-inner">
          <div className="header-left">
            <button className="hamburger-btn" onClick={() => setDrawerOpen(true)}>
              ☰
            </button>
            <div className="header-page-title">
              {currentNav?.icon} {currentNav?.label}
            </div>
          </div>
          <div className="logo">
            <span className="logo-icon">🏠</span>
            <span className="logo-text">{household.name}</span>
          </div>
        </div>
      </header>

      {/* Drawer overlay */}
      {drawerOpen && <div className="drawer-overlay" onClick={() => setDrawerOpen(false)} />}

      {/* Side drawer */}
      <div className={`drawer ${drawerOpen ? "open" : ""}`} ref={drawerRef}>
        <div className="drawer-header">
          <div className="drawer-logo">
            <span>🏠</span>
            <span className="drawer-household">{household.name}</span>
          </div>
          <button className="drawer-close" onClick={() => setDrawerOpen(false)}>✕</button>
        </div>

        <nav className="drawer-nav">
          {NAV_ITEMS.map((item) => (
            <button
              key={item.key}
              className={`drawer-nav-item ${page === item.key ? "active" : ""}`}
              onClick={() => handleNavClick(item.key)}
            >
              <span className="drawer-nav-icon">{item.icon}</span>
              <span className="drawer-nav-label">{item.label}</span>
              {item.key === "shared" && newSharedCount > 0 && (
                <span className="drawer-badge">{newSharedCount}</span>
              )}
            </button>
          ))}
        </nav>

        <div className="drawer-footer">
          <button className="drawer-settings-btn" onClick={() => { setShowSettings(true); setDrawerOpen(false); }}>
            ⚙️ Settings
          </button>
        </div>
      </div>

      <main className="main">
        {page === "inventory" && <InventoryPage />}
        {page === "cart" && <CartPage />}
        {page === "meals" && <MealsPage />}
        {page === "mealplan" && <MealPlanPage />}
        {page === "shared" && <SharedPage />}
        {page === "history" && <HistoryPage />}
      </main>

      {showSettings && (
        <HouseholdSettings
          household={household}
          onClose={() => setShowSettings(false)}
          onLeave={handleLeave}
        />
      )}
    </div>
  );
}
